import { Component, inject, input } from '@angular/core';
import { BuildingItemDto, BuildingService, RegionItemDto, TeamItemDto, TeamService } from '@artemis-software/wr-api';
import { NbToastrService } from '@nebular/theme';
import * as L from 'leaflet';
import { watch } from '@/utils/watch';

@Component({
  standalone: false,
  selector: 'wr-region-dashboard-map',
  templateUrl: './region-dashboard-map.component.html',
  styleUrls: ['./region-dashboard-map.component.scss'],
})
export class RegionDashboardMapComponent {
  readonly region = input<RegionItemDto | undefined>();
  readonly teamService = inject(TeamService);
  readonly buildingService = inject(BuildingService);
  readonly toastr = inject(NbToastrService);

  private map?: L.Map;
  private layerGroup?: L.LayerGroup;

  constructor() {
    watch(this.region, (region) => {
      this.clearMap();
      if (region?.id) {
        this.teamService.getTeamsByRegionId(region.id).subscribe({
          next: (teams) => this.loadAllBuildings(teams),
          error: () => this.toastr.danger('Teams konnten nicht geladen werden.', 'Fehler beim Laden'),
        });
      }
    });
  }

  private loadAllBuildings(teams: TeamItemDto[]) {
    const buildingRequests = teams.map(team =>
      this.buildingService.getBuildingsByTeamId(team.id)
        .toPromise()
        .then(buildings =>
          (buildings ?? []).map(building => ({ building, team }))
        )
        .catch(error => {
          this.toastr.danger(`Gebäude für Team ${team.name} konnten nicht geladen werden.`, 'Fehler beim Laden');
          return [];
        })
    );

    Promise.all(buildingRequests)
      .then(results => {
        const all = results.flat();
        this.initializeMap(all);
      })
      .catch(() => {
        this.toastr.danger('Gebäude konnten nicht geladen werden.', 'Fehler beim Laden');
      });
  }


  private initializeMap(data: { building: BuildingItemDto, team: TeamItemDto }[]) {
    if (data.length === 0) {
      this.toastr.warning('Keine Gebäude für diese Region gefunden.', 'Keine Daten');
      return;
    }

    this.map = L.map('map');
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(this.map);

    this.layerGroup = L.layerGroup().addTo(this.map);

    for (const { building, team } of data) {
      const coords = building.baseData.coordinates;
      if (
        coords &&
        coords.latitude !== undefined &&
        coords.longitude !== undefined &&
        !(Math.abs(coords.latitude) <= 0 && Math.abs(coords.longitude) <= 0)
      ) {
        const marker = L.circleMarker([coords.latitude, coords.longitude], {
          radius: 8,
          color: team.color,
          fillColor: team.color,
          fillOpacity: 0.9,
          weight: 2,
        }).bindPopup(`<b>${building.baseData.name}</b><br/>Team: ${team.name}`);
        marker.addTo(this.layerGroup);
      }
    }

    const bounds = L.latLngBounds(
      data
        .filter(({ building }) =>
          building.baseData.coordinates && building.baseData.coordinates.latitude !== undefined && building.baseData.coordinates.longitude !== undefined
        )
        .map(({ building }) =>
          [building.baseData.coordinates!.latitude, building.baseData.coordinates!.longitude] as [number, number]
        )
    );

    if (bounds.isValid()) {
      this.map.fitBounds(bounds, { padding: [50, 50] });
    }
  }

  private clearMap() {
    if (this.layerGroup) {
      this.layerGroup.clearLayers();
      this.layerGroup.remove();
      this.layerGroup = undefined;
    }
    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
  }
}
